import { useEffect, useRef, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAndSetReportData } from '../redux/actions/reports'
import { getTaskSummary } from '../redux/actions/tasks_v2'
import { ReportConfig } from '../redux/reducers/reports'
import { IPromptInjection, RootState } from 'constants/interfaces'
import { useReportResponse } from './useReportResponse'
import { useDeepDiveReports } from './useDeepDiveReports'
import { combineMultipleReports } from '../utils/combineReports'
import { isEmpty, isEqual } from 'lodash'
import { PROMPT_INJECTION_TYPE } from 'constants/enums'

export const useFirstUserMessageInChatGPT = ({
  isInjectReports,
  isInstantConversation,
  webSocket,
  isWebSocketReady,
  setIsBotResponseInProgress,
  prompt_id,
  initMessageResetTrigger,
  setPendingReportData,
  lastInteractionDate,
  mode,
  group_id,
  customInitData,
  customInitImages,
  promptInjections,
}: {
  isInjectReports: boolean
  isInstantConversation?: boolean
  webSocket: WebSocket | null
  isWebSocketReady: boolean
  setIsBotResponseInProgress: React.Dispatch<React.SetStateAction<boolean>>
  prompt_id?: string
  initMessageResetTrigger?: number
  setPendingReportData?: React.Dispatch<React.SetStateAction<string | null>>
  lastInteractionDate?: number | null
  mode: number
  group_id: string | null
  customInitData?: string
  customInitImages?: string[]
  promptInjections?: IPromptInjection[]
}) => {
  const dispatch = useDispatch()

  const report_config_new: ReportConfig | null = useSelector(
    ({ config: { retailConfig } }: RootState) => retailConfig?.report_config_new || null,
    isEqual
  )
  const activeStoreId = useSelector(({ config: { activeGroupID } }: RootState) => activeGroupID, isEqual)
  const reports = useSelector(({ reports }: RootState) => reports.reports, isEqual)
  const userReport = activeStoreId ? reports[activeStoreId]?.[0] ?? null : null

  // Additional selectors for deep dive reports
  const { retailConfig } = useSelector((state: RootState) => state.config)
  const { selectedUsersIds, selectedTag, selectedDataFilter } = useSelector((state: RootState) => state.reports)

  const [isReportFetchingDone, setIsReportFetchingDone] = useState(!isInjectReports)
  const [hasReportFetchStarted, setHasReportFetchStarted] = useState(false)
  const reportFetchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasInitMessageSent = useRef(false)
  const [areReportsFresh, setAreReportsFresh] = useState(false)

  // Task summary injection state
  const firstInjection = !isInjectReports ? promptInjections?.[0] ?? null : null
  const isTaskSummaryInjection = firstInjection?.type === PROMPT_INJECTION_TYPE.TASK_SUMMARY
  const taskSummaryFetchStarted = useRef(false)
  // undefined = pending fetch; '' = not needed or fetch returned nothing; string = ready
  const [taskSummaryString, setTaskSummaryString] = useState<string | undefined>(
    isTaskSummaryInjection ? undefined : ''
  )

  // Check if reports are still fresh (within 10 minutes)
  const isReportsStillFresh = useCallback(() => {
    if (!lastInteractionDate) return false

    const now = new Date().getTime()
    const timePassedInSeconds = (now - lastInteractionDate) / 1000
    const TEN_MINUTES = 10 * 60

    return timePassedInSeconds < TEN_MINUTES
  }, [lastInteractionDate])

  // Update fresh state when dependencies change
  useEffect(() => {
    setAreReportsFresh(isReportsStillFresh())
  }, [isReportsStillFresh])

  // Deep dive reports hook
  const { deepDiveResults, isLoading: isDeepDiveLoading } = useDeepDiveReports({
    reportConfig: report_config_new,
    enabled: isInjectReports && isReportFetchingDone && !areReportsFresh,
    selectedReportRangeType: retailConfig?.data_range_types[0],
    selectedUsersIds,
    tag: selectedTag,
    targetDataFilter: selectedDataFilter,
    subRetailId: retailConfig?.sub_retail_id || undefined,
    targetGroupId: activeStoreId || undefined,
  })

  // Process all reports through useMultipleReportResponse
  const mainReportString = useReportResponse({
    reportConfig: report_config_new,
    reportData: userReport,
  })
  const deepDiveReportStrings = deepDiveResults.map((result) => result.data)
  // Combine all report strings
  const combinedReportString = combineMultipleReports([mainReportString, ...deepDiveReportStrings])

  // Reset init message flag when reset trigger changes
  useEffect(() => {
    if (initMessageResetTrigger && initMessageResetTrigger > 0) {
      hasInitMessageSent.current = false
      console.log('Init message flag reset due to reconnection')
    }
  }, [initMessageResetTrigger])

  // Fetch task summary when PROMPT_INJECTION_TYPE.TASK_SUMMARY injection is present
  useEffect(() => {
    if (!isTaskSummaryInjection || taskSummaryFetchStarted.current) return

    taskSummaryFetchStarted.current = true
    const tid = (firstInjection as IPromptInjection).params?.tid

    if (!tid) {
      setTaskSummaryString('')
      return
    }

    let cancelled = false
    const fetchSummary = async () => {
      const response = await (dispatch(getTaskSummary(tid)) as unknown as Promise<Record<string, any> | null>)
      if (!cancelled) {
        setTaskSummaryString(response !== null ? JSON.stringify(response) : '')
      }
    }

    fetchSummary()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Start fetching report if needed (for non-instant conversation only)
  useEffect(() => {
    if (isInstantConversation || !isInjectReports || !activeStoreId || hasReportFetchStarted) {
      return
    }

    if (isEmpty(report_config_new)) {
      setIsReportFetchingDone(true)
      return
    }

    // Check if reports are still fresh before fetching new ones
    const reportsAreFresh = areReportsFresh

    if (reportsAreFresh) {
      console.log('Reports are still fresh for non-instant conversation, skipping fetch')
      setIsReportFetchingDone(true)
      return
    }

    setHasReportFetchStarted(true)

    reportFetchTimeoutRef.current = setTimeout(() => {
      setIsReportFetchingDone(true)
    }, 5000)
    dispatch(
      fetchAndSetReportData({
        groupId: activeStoreId,
        users: [],
        report_range_type: 1,
        reportDataKey: activeStoreId,
        sub_retail_id: '',
        reportsHandlerFunction: () => {
          if (reportFetchTimeoutRef.current) {
            clearTimeout(reportFetchTimeoutRef.current)
            reportFetchTimeoutRef.current = null
          }
          setIsReportFetchingDone(true)
        },
      })
    )
  }, [
    isInjectReports,
    activeStoreId,
    dispatch,
    hasReportFetchStarted,
    report_config_new,
    isInstantConversation,
    areReportsFresh,
  ])

  // For instant conversation mode: send empty is_init and start loading reports
  useEffect(() => {
    const canSend = isWebSocketReady && webSocket?.readyState === WebSocket.OPEN && !hasInitMessageSent.current

    if (!canSend || !isInstantConversation) return

    // Send is_init message immediately (with custom data/images if provided)
    const messagePayload: any = {
      action: 'ws_conversation',
      is_init: true,
      ...(prompt_id ? { prompt_id } : {}),
      mode,
      group_id,
      ...(customInitData ? { is_stream: false } : {}),
      ...(customInitData ? { message: { data: customInitData } } : {}),
      ...(customInitImages && customInitImages.length > 0 ? { images: customInitImages } : {}),
    }

    console.log('Sending is_init message for instant conversation', customInitData ? 'with custom data' : '')
    webSocket.send(JSON.stringify(messagePayload))
    hasInitMessageSent.current = true
    setIsBotResponseInProgress(true)

    // Check if reports are still fresh before fetching new ones
    const reportsAreFresh = areReportsFresh

    // Start loading reports for next message only if not fresh
    if (isInjectReports && activeStoreId && !isEmpty(report_config_new) && !hasReportFetchStarted && !reportsAreFresh) {
      console.log('Starting report fetch for second message (reports not fresh)')
      setHasReportFetchStarted(true)
      console.log('fetch 2')
      dispatch(
        fetchAndSetReportData({
          groupId: activeStoreId,
          users: [],
          report_range_type: 1,
          reportDataKey: activeStoreId,
          sub_retail_id: '',
          reportsHandlerFunction: () => {
            console.log('Reports loaded for instant conversation')
            setIsReportFetchingDone(true)
          },
        })
      )
    } else if (reportsAreFresh) {
      console.log('Reports are still fresh, skipping fetch')
      setIsReportFetchingDone(true)
    }
  }, [
    isWebSocketReady,
    webSocket,
    prompt_id,
    isInstantConversation,
    isInjectReports,
    activeStoreId,
    report_config_new,
    hasReportFetchStarted,
    dispatch,
    setIsBotResponseInProgress,
    areReportsFresh,
    mode,
    group_id,
    customInitData,
    customInitImages,
  ])

  // Process and save reports for instant conversation
  useEffect(() => {
    if (!isInstantConversation || !setPendingReportData) return

    // Wait for all report data to be available
    if (!isReportFetchingDone || !userReport || isDeepDiveLoading) return

    if (combinedReportString !== undefined) {
      const validReport = typeof combinedReportString === 'string' && combinedReportString.trim() !== ''
      if (validReport) {
        console.log('Saving report data for next user message')
        setPendingReportData(combinedReportString)
      }
    }
  }, [
    isInstantConversation,
    isReportFetchingDone,
    isDeepDiveLoading,
    combinedReportString,
    setPendingReportData,
    userReport,
  ])

  // Send is_init message (original flow for non-instant conversation)
  useEffect(() => {
    const canSend = isWebSocketReady && webSocket?.readyState === WebSocket.OPEN && !hasInitMessageSent.current

    if (!canSend || isInstantConversation) return

    // If we need to inject reports, wait for them to be loaded
    if (isInjectReports && !isReportFetchingDone) {
      console.log('Waiting for base reports to load before sending first message...')
      return
    }

    // Additional check: if we need reports but don't have userReport data yet
    if (isInjectReports && !userReport) {
      console.log('Waiting for userReport data to be available...')
      return
    }

    // Wait for deep dive reports to finish loading
    if (isInjectReports && isDeepDiveLoading) {
      console.log('Waiting for deep dive reports to load before sending first message...')
      return
    }

    // Wait for all report strings to be processed
    if (isInjectReports) {
      const hasUndefinedStrings = combinedReportString === undefined

      if (hasUndefinedStrings) {
        console.log('Waiting for all report strings to be processed...')
        return
      }
    }

    // Wait for task summary if needed
    if (isTaskSummaryInjection && taskSummaryString === undefined) {
      console.log('Waiting for task summary to load before sending first message...')
      return
    }

    const messagePayload: any = {
      action: 'ws_conversation',
      is_init: true,
      ...(prompt_id ? { prompt_id } : {}),
      mode,
      group_id,
    }

    if (isInjectReports) {
      const validReport = typeof combinedReportString === 'string' && combinedReportString.trim() !== ''

      if (validReport) {
        const testPayload = {
          ...messagePayload,
          message: { data: combinedReportString },
        }
        const messageSize = new TextEncoder().encode(JSON.stringify(testPayload)).length
        const MAX_CHUNK_SIZE = 120 * 1024

        if (messageSize > MAX_CHUNK_SIZE) {
          console.warn(
            `Report data is too large (${messageSize} bytes > ${MAX_CHUNK_SIZE} bytes). Consider using chunking or truncating the data.`
          )
          messagePayload.message = {
            data: combinedReportString.substring(0, MAX_CHUNK_SIZE * 0.8) + '\n\n[Data truncated due to size limits]',
          }
        } else {
          messagePayload.message = { data: combinedReportString }
        }

        console.log(`Sending first message with combined report data (${messageSize} bytes)`)
      } else {
        console.log('No valid report data, sending empty is_init')
      }
    } else if (isTaskSummaryInjection && taskSummaryString) {
      messagePayload.message = { data: taskSummaryString }
      console.log('Sending first message with task summary data')
    }

    webSocket.send(JSON.stringify(messagePayload))
    hasInitMessageSent.current = true
    setIsBotResponseInProgress(true)
  }, [
    isWebSocketReady,
    webSocket,
    prompt_id,
    isInjectReports,
    isReportFetchingDone,
    isDeepDiveLoading,
    combinedReportString,
    setIsBotResponseInProgress,
    userReport,
    deepDiveResults,
    isInstantConversation,
    isTaskSummaryInjection,
    taskSummaryString,
    mode,
    group_id,
  ])

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (reportFetchTimeoutRef.current) {
        clearTimeout(reportFetchTimeoutRef.current)
        reportFetchTimeoutRef.current = null
      }
    }
  }, [])
}
